<template>
  <div data-view="users">
    <router-view />

    <layout-logged-frame :title="$t('users')" has-before>
      <template v-slot:header-actions>
        <Can action="store" subject="user">
          <v-btn
            color="primary"
            @click="$router.push({ name: 'pending-users-new' })"
          >
            <v-icon>add</v-icon>
            {{ $t('add_new_user') }}
          </v-btn>
        </Can>
      </template>

      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="usersCount"
        />
      </template>

      <template v-slot:content>
        <UsersFilters v-if="hasFilters" @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :columns="pendingColumns"
          :items="pendingUsers"
          :pages="totalPendingPages"
          :pagination="pendingPagination"
          :is-loading="isPendingLoading"
          has-custom-items-template
          :on-select-item="selectUser"
          :on-update-pagination="onUpdatePendingPagination"
          :on-change-page="onChangePendingPage"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'status.name'">
              <StatusPanel
                table-group="users"
                :status="scope.cell.item.status.name"
              />
            </template>
            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import { cloneDeep } from 'lodash-es';
  import UsersFilters from '../../components/forms/filters/User';
  import StatusPanel from '../../components/panels/Status';
  import { isStr, isNum, extractNestedProp } from '../../utils';
  import columns from '../../config/users/columns';
  import navigationTabs from '../../config/users/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs';

  const pendingColumns = cloneDeep(columns).filter(({ text }) => text !== 'Status');

  export default {
    components: {
      UsersFilters,
      StatusPanel,
      NavigationTabs
    },
    data() {
      return {
        columns,
        navigationTabs,
        pendingColumns,
        usersCount: [],
        pendingPagination: {
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          order: 'asc' ,
          totalItems: 0,
        },
        totalPendingPages: 0,
        pendingQuery: {},
        isPendingLoading: false,
        isInitialPendingUsersCall: true,
      };
    },
    computed: {
      pendingUsers() {
        return this.$store.getters['users/pendingItems'];
      },
      totalPendingCount() {
        return this.pendingPagination.totalItems || 0;
      },
      hasFilters() {
        return (
          this.$ability.can('see-user-status', 'filter') ||
          this.$ability.can('see-user-role', 'filter') ||
          this.$ability.can('see-user-search', 'filter') ||
          this.$ability.can('see-user-business', 'filter')
        );
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (
          to.query?.status === 'success' &&
          (from.name === 'pending-users-new' || from.name === 'pending-users-edit') &&
          to.name === 'users-pending'
        ) {
          this.getPendingUsers(this.pendingQuery);
        }
      },
    },
    created() {
      this.getUsersCount();
    },
    methods: {
      getUsersCount() {
        this.$store.dispatch('users/getUsersCount')
          .then(res => { this.usersCount = res })
          .catch(err => err);
      },
      getPendingUsers({ page = 1, order = 'asc', sort = '', search = '', role = '', businessId = -1, status = '' } = {}) {
        const query = {
          page,
          order,
          sort,
          search,
          role,
          businessId,
          status
        };

        this.isPendingLoading = true;
        this.pendingPagination.order = order;

        return this.$store
          .dispatch('users/getPendingItems', query)
          .then((res) => {
            const { meta } = this.$store.getters['users/pendingItemsData'];

            this.pendingPagination.page = meta?.current_page || 0;
            this.pendingPagination.totalItems = meta?.total || 0;
            this.totalPendingPages = meta?.last_page || 0;
            this.isPendingLoading = false;

            return res;
          })
          .catch((err) => {
            this.isPendingLoading = false;
            return err;
          });
      },
      selectUser(user) {
        this.$store.dispatch('users/setActiveItem', user);
        this.$router.push({ name: 'pending-users-edit', params: { id: user.id } });
      },
      onUpdatePendingPagination({ page, order, sortBy }) {
        const { pendingQuery, pendingPagination } = this;
        const nextQuery = {
          ...pendingQuery,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pendingPagination.sortBy,
        };

        this.pendingQuery = nextQuery;

        this.getPendingUsers(nextQuery);
      },
      onChangePendingPage(page) {
        const { pendingQuery, pendingPagination } = this;
        const { order, sortBy } = pendingPagination;
        const nextQuery = {
          ...pendingQuery,
          page,
          order,
          sort: sortBy,
        };

        this.pendingQuery = nextQuery;

        this.getPendingUsers(nextQuery);
      },
      onFilter(filters) {
        this.pendingQuery = {
          ...this.pendingQuery,
          ...this.getSearchQuery(filters),
        };

        this.getPendingUsers(this.pendingQuery);
      },
      onClearFilters() {
        const { sortBy } = this.pendingPagination;
        const nextQuery = { sort: sortBy };
        this.pendingQuery = nextQuery;

        this.getPendingUsers(this.pendingQuery);
      },
      getSearchQuery({ search, business, role, status }) {
        const query = {};

        query.search = search;
        query.businessId = isNum(business) ? business : -1;
        query.role = isStr(role) ? role : '';
        query.status = isNum(status) ? status : '';

        return query;
      },
      displayRoleColumn(item) {
        return item.role.name;
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

.v-datatable {
  min-width: $table-min-width;
}
</style>
